import React, { useState, useEffect } from 'react';
import PostBrandLabel from 'components/PostBrandLabel/PostBrandLabel';
import {
  MDBIcon,
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBTypography
} from 'mdbreact';

import "./style.scss";
import { PlatformIcon as SharedPlatformIcon, pickPostThumb, formatPostTime, PubTypeBadge, StatusBadge as SharedStatusBadge, PostThumb, postPlatforms } from 'helpers/postDisplay';
import { canDeletePost } from 'helpers/postDeletable';
import { archiveControl } from 'helpers/postArchive';

// Re-export the shared icon so existing imports from this file
// (KanbanCard pulls PlatformIcon from here) keep working.
export const PlatformIcon = SharedPlatformIcon;

// Local wrapper: forwards to the shared StatusBadge. The old badge
// hardcoded bidlight-era statuses (pending_subject / approved_subject)
// that never matched the Prigmar Post.queueStatus enum, so every
// post showed the fallback "pending". The shared badge reads
// queueStatus → label/color directly.
const StatusBadge = ({ item, source }) => <SharedStatusBadge item={item} source={source} size={10} />;

// Post content preview component
// Compact content preview for a calendar day cell. Now renders the
// post's actual thumbnail (image or video poster) when present, so the
// month grid shows visuals at a glance — not just a text icon.
const PostContentPreview = ({ post }) => {
  if (!post) return null;
  const thumb = pickPostThumb(post);
  return (
    <div>
      {thumb && (
        <div style={{
          width: '100%', maxHeight: 70, marginBottom: 4,
          borderRadius: 4, overflow: 'hidden', background: '#000', position: 'relative',
        }}>
          <PostThumb post={post}
                     style={{ width: '100%', maxHeight: 70, objectFit: 'cover', display: 'block' }} />
          {thumb.isVideo && (
            <span style={{
              position: 'absolute', top: 4, right: 4, padding: '1px 6px',
              background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 3, fontSize: 9, fontWeight: 600,
            }}>VIDEO</span>
          )}
        </div>
      )}
      {/* Wrap the text (not single-line truncate) so long titles +
          descriptions stay readable. Cap at 4 lines via WebKit
          line-clamp so cards don't grow unboundedly in tall months. */}
      <p className="mb-0" style={{
        fontSize: 12, lineHeight: 1.4,
        display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
        overflow: 'hidden', wordBreak: 'break-word',
      }}>
        {post.textContent || post.description || ''}
      </p>
    </div>
  );
};

// Small per-card Archive / un-archive control, shared with the List + Kanban
// views via helpers/postArchive. On a live post it archives it; on an archived
// post the same button sends it back to "awaiting approval". Post-only — wired
// only when the parent passes onArchive.
const ArchiveToggle = ({ post, onArchive, fontSize = 12, style = {} }) => {
  if (!onArchive) return null;
  const a = archiveControl(post);
  return (
    <span
      role="button"
      title={a.title}
      onClick={(e) => { e.stopPropagation(); onArchive(post); }}
      style={{ cursor: 'pointer', fontSize, lineHeight: 1, color: a.archived ? '#7c3aed' : '#9aa0a6', ...style }}
    >
      {a.label}
    </span>
  );
};

// Brand day cell component
const DayCell = ({ date, onEdit, onDelete, onArchive, posts = [], source }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!date) return null;

  return (
    <div className="border p-2" style={{ minHeight: '120px' }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="font-weight-bold">{date.getDate()}</span>
        {posts.length > 0 && (
          <MDBBadge color="info" pill>
            {posts.length}
          </MDBBadge>
        )}
      </div>
      
      <div>
        {Array.isArray(posts) && posts.map((post, index) => (
          <MDBCard
            key={post?._id || index}
            className="mb-2 maxw-200 cursor-pointer"
            style={{ position: 'relative' }}
            onClick={() => onEdit(post)}
          >
            {/* Delete affordance, top-right. Hidden for posts that
                have already been published (deleting the row would
                only hide the audit trail; the share is still live on
                the platform). Ideas have no posted state, so always
                show when onDelete is wired. stopPropagation prevents
                the click from also opening the edit modal. */}
            {onDelete && (source !== 'post' || canDeletePost(post)) && (
              <MDBIcon
                icon="trash"
                className="text-danger"
                style={{ position: 'absolute', top: 6, right: 6, cursor: 'pointer', fontSize: 12, zIndex: 1 }}
                onClick={(e) => { e.stopPropagation(); onDelete(post); }}
              />
            )}
            {source === 'post' && onArchive && (
              <ArchiveToggle
                post={post}
                onArchive={onArchive}
                style={{ position: 'absolute', top: 6, right: (onDelete && canDeletePost(post)) ? 26 : 6, zIndex: 1 }}
              />
            )}
            <MDBCardBody className="p-2">
              <div className="d-flex justify-content-between align-items-center">
                <StatusBadge item={post} source={source} />
                {source === 'post' && <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <PubTypeBadge type={post?.type} size={9} />
                  {/* Resolve the targeted platforms through the shared helper so
                      AUTO-GENERATED posts (which carry only socialPlatforms slugs,
                      not populated targetedSocialAccounts) render their logos on
                      the very first draw — same as manually-targeted posts. */}
                  {postPlatforms(post).map((p, i) =>
                    <PlatformIcon key={i} platform={p} size={12} />
                  )}
                </div>}
              </div>
              
              {/* Always show the media thumbnail when the post has one,
                  expanded or collapsed. The expanded view adds the full
                  text content + footer underneath. */}
              <PostContentPreview post={post} />
              {isExpanded ? (
                <div className="mt-1">
                  <MDBTypography tag="h6" className="mb-1" style={{ fontSize: 13 }}>
                    {source === 'post' && <PostBrandLabel brandId={post.brand_id} brandName={post.brandName} />}
                    {post?.title || 'Untitled Post'}
                  </MDBTypography>
                  <div className="d-flex justify-content-between align-items-center mt-2 text-muted">
                    <small style={{ fontSize: 11 }}>
                      {formatPostTime(source === 'post' ? post.scheduled_at : post.createdAt)}
                    </small>
                    <div>
                      <MDBIcon icon="share-alt" className="mx-1" />
                      <MDBIcon icon="check-circle" className="mx-1" />
                      <MDBIcon icon="book-open" className="mx-1" />
                    </div>
                  </div>
                </div>
              ) : (
                /* Wrap the title (up to 3 lines) so it isn't clipped
                   mid-word on cards that fit a longer title. */
                <div className="mt-1" style={{
                  fontSize: 12, fontWeight: 600, lineHeight: 1.35,
                  display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden', wordBreak: 'break-word',
                }}>
                  {source === 'post' && <PostBrandLabel brandId={post.brand_id} brandName={post.brandName} />}
                  {post?.title || 'Untitled Post'}
                </div>
              )}
            </MDBCardBody>
          </MDBCard>
        ))}
      </div>
    </div>
  );
};

// Main brand component
const SocialMediaBrand = ({ posts = [], onEdit, onDelete, onArchive, source='post' }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [brandDays, setBrandDays] = useState([]);

  useEffect(() => {
    const generateBrandDays = () => {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const days = [];
      const startingDay = firstDay.getDay();
      
      // Add previous month's days
      for (let i = 0; i < startingDay; i++) {
        const day = new Date(year, month, -startingDay + i + 1);
        days.push(day);
      }
      
      // Add current month's days
      for (let i = 1; i <= lastDay.getDate(); i++) {
        const day = new Date(year, month, i);
        days.push(day);
      }
      
      // Add next month's days to complete the grid
      const remainingDays = 42 - days.length; // 6 rows × 7 days
      for (let i = 1; i <= remainingDays; i++) {
        const day = new Date(year, month + 1, i);
        days.push(day);
      }
      
      setBrandDays(days);
    };

    generateBrandDays();
  }, [currentDate]);

  const getPostsForDate = (date) => {
    if (!Array.isArray(posts) || !date) return [];
    
    return posts.filter(post => {
      if (source === 'post' && !post?.scheduled_at) return false;
      const postDate = new Date(source === 'post' ? post.scheduled_at : post.createdAt);
      return (
        postDate.getDate() === date.getDate() &&
        postDate.getMonth() === date.getMonth() &&
        postDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + direction)));
  };

  return (
    <MDBCard>
      <MDBCardBody>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <MDBIcon icon="calendar" className="mr-2" />
            <h4 className="mb-0">
              {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </h4>
          </div>
          <div className='today-div'>
            <MDBBtn 
              color="light"
              size="sm"
              onClick={() => navigateMonth(-1)}
              className="mr-2"
            >
              <MDBIcon icon="chevron-left" />
            </MDBBtn>
            <MDBBtn
              color="primary"
              size="sm"
              onClick={() => setCurrentDate(new Date())}
              className="mr-2"
            >
              Today
            </MDBBtn>
            <MDBBtn
              color="light"
              size="sm"
              onClick={() => navigateMonth(1)}
            >
              <MDBIcon icon="chevron-right" />
            </MDBBtn>
          </div>
        </div>

        <div className="calendar-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center font-weight-bold p-2 border-bottom">
              {day}
            </div>
          ))}
          
          {brandDays.map((date, index) => (
            <DayCell
              source={source}
              key={index}
              date={date}
              onEdit={onEdit}
              onDelete={onDelete}
              onArchive={onArchive}
              posts={getPostsForDate(date)}
            />
          ))}
        </div>
      </MDBCardBody>
    </MDBCard>
  );
};

export default SocialMediaBrand;